/**
 * /fr/comparisons/notion-ai — Draftlize vs Notion (French). Links to
 * /fr/journal-de-decisions.
 */
import { MarketingShell } from '../MarketingShell';

export function NotionAiFr() {
  return (
    <MarketingShell
      currentLocale="fr"
      hreflangPath="/comparisons/notion-ai"
      hreflangLocales={['fr']}
      documentTitle="Alternative à Notion pour le produit : Draftlize vs Notion · Draftlize"
      documentDescription="Notion stocke des pages ; Draftlize est un graphe de cartes qu'un agent IA lit et écrit, pour que les décisions ne dérivent pas. Comparatif côte à côte."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Comparatif · vs Notion</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            Draftlize vs Notion :<br />
            <em>lequel pour votre travail produit ?</em>
          </h1>
          <p className="lede">
            Réponse courte : Notion est le meilleur outil pour noter les choses. Draftlize est un substrat où les agents IA peuvent <em>lire et écrire</em> votre réflexion produit sous forme de cartes structurées. Ils résolvent des problèmes voisins : plutôt que de remplacer l'un par l'autre, il s'agit de savoir lequel vous faut vraiment aujourd'hui.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">L'essentiel</span>
            <h2 className="serif">Côte à côte, <em>7 dimensions.</em></h2>
          </div>
          <p>À partir de la même feuille de route et des mêmes scénarios exécutés dans les deux outils.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Notion</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Forme du substrat</td><td className="ours yes">Graphe de cartes typées</td><td className="partial">Pages de blocs</td></tr>
            <tr><td>L'IA lit les décisions comme contexte à chaque fois</td><td className="ours yes">natif</td><td className="no">—</td></tr>
            <tr><td>L'IA réécrit sous forme de cartes structurées</td><td className="ours yes">natif</td><td className="partial">écrit des blocs, sans structure</td></tr>
            <tr><td>Propagation « stale » quand une dépendance change</td><td className="ours yes">natif</td><td className="no">—</td></tr>
            <tr><td>Docs riches et bases de données</td><td className="no">—</td><td className="yes">excellent</td></tr>
            <tr><td>Wiki d'équipe · permissions · collaboration</td><td className="no">solo (v1)</td><td className="yes">excellent</td></tr>
            <tr><td>Intégration Claude Code / MCP</td><td className="ours yes">natif</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Là où <em>Notion gagne.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">S'il vous faut un wiki</h3>
            <p>Notion est le meilleur wiki qui existe. Si votre problème est « où l'équipe range-t-elle le runbook », c'est Notion. Draftlize ne cherche pas à être un wiki.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Si vous vivez dans les tables et bases</h3>
            <p>Le système de bases et de vues de Notion est imbattable. Suivi de roadmap, grilles de sprint, tableaux d'évaluation : pour ces formes, les collections structurées de Notion l'emportent toujours.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Si votre équipe est grande</h3>
            <p>Collaboration en temps réel, permissions fines, commentaires, mentions : Notion est fait pour ça. Draftlize est solo par conception.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Là où <em>Draftlize gagne.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Une IA qui <em>se souvient</em> vraiment</h3>
            <p>La « mémoire » de Notion AI est une récupération best-effort vers le chat. Chez Draftlize, le substrat où travaille l'agent, ce sont les cartes : il lit votre décision de prix à chaque échange, automatiquement. La réexplication tombe à zéro.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">La dérive devient visible</h3>
            <p>Vous modifiez une page Notion → personne ne le remarque. Vous modifiez une carte de décision Draftlize → chaque carte dépendante passe en <code>stale</code>. Le substrat suit l'invalidation comme un système de build.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Contexte de premier ordre pour Claude Code / Cursor / MCP</h3>
            <p>Notion AI vit dans Notion. Les cartes Draftlize arrivent dans votre repo via la CLI et sont exposées par MCP à chaque outil IA. Les décisions sont adressables depuis n'importe lequel. Poursuivez avec le <a href="/fr/journal-de-decisions" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>journal de décisions</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Notion répond à « où l'équipe note-t-elle les choses ». Draftlize répond à « où la réflexion produit se capitalise quand l'IA fait la moitié du travail ».
        </blockquote>
        <cite>Si vous n'avez besoin que de la première réponse, Notion suffit.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Commencez gratuitement avec <em>5 $.</em></h2>
        </div>
        <div className="trust-strip">
          <span>5 $ offerts à l'inscription</span>
          <span>Payez seulement ce que vous utilisez</span>
          <span>Le solde n'expire jamais</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Vous pouvez garder Notion. Draftlize est le substrat sous les parties assistées par l'IA. 5 $ offerts à l'inscription, payez seulement ce que vous utilisez, le solde n'expire jamais.
          </p>
          <a className="btn-primary" href="/">Commencer gratuitement avec 5 $</a>
        </div>
      </section>
    </MarketingShell>
  );
}
